import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Group, Menu, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useAgencies } from '@/queries/agencies/useAgencies';
import { useCurrentAgency } from '@/queries/agencies/useCurrentAgency';
import { useAgencyAbilities } from '@/queries/me/useAgencyAbilities';
import { useMe } from '@/queries/me/useMe';
import { useSetActiveAgency } from '@/queries/me/useSetActiveAgency';
import { useStopImpersonating } from '@/queries/me/useStopImpersonating';
import { Icon } from '@/ui';
import { AgencyAvatar } from '../Agencies/AgencyAvatar';
import { ClientModal } from '../Clients/ClientList/ClientModal';
import classes from './AgencyButton.module.css';

export const AgencyButton = () => {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { mutateAsync: setActiveAgency } = useSetActiveAgency();
  const { mutateAsync: stopImpersonating } = useStopImpersonating();
  const { data: agency } = useCurrentAgency();
  const { data: agencies = [] } = useAgencies();
  const [agencyPopoverOpened, { toggle: toggleAgencyPopover, close: closeAgencyPopover }] =
    useDisclosure(false);
  const [addClientOpened, { toggle: toggleAddClient, close: closeAddClient }] =
    useDisclosure(false);

  const agencyPolicies = useAgencyAbilities();

  return (
    <>
      <Menu width="target" opened={agencyPopoverOpened} onClose={closeAgencyPopover}>
        <Menu.Target>
          <UnstyledButton className={classes.agency_btn} onClick={toggleAgencyPopover}>
            <Group gap="xs" wrap="nowrap" justify="center" align="center">
              {agency && <AgencyAvatar size={24} agency={agency} />}
              <Text size="sm" fw={500} truncate flex={1}>
                {agency?.name}
              </Text>
              <Icon
                name="chevronDown"
                className={classes.chevron}
                data-opened={agencyPopoverOpened ? true : undefined}
              />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown miw={250}>
          <Menu.Label>{t('navbar.agency')}</Menu.Label>
          <Menu.Item component={Link} to="/settings/agency">
            <Group gap="3xs">
              <Icon name="settings" />
              <Text size="sm" fw={500}>
                {t('navbar.settings')}
              </Text>
            </Group>
          </Menu.Item>
          {/* Switch agency submenu */}
          {agencies.length > 1 && (
            <>
              <Menu.Label>{t('navbar.switch-agency')}</Menu.Label>
              {agencies
                .filter((a) => a.status === 'accepted')
                .map((membershipAgency) => (
                  <Menu.Item
                    key={membershipAgency.id}
                    onClick={async () => {
                      await setActiveAgency(membershipAgency.id);
                      closeAgencyPopover();
                      window.location.reload();
                    }}
                  >
                    <Group gap="3xs" wrap="nowrap">
                      <AgencyAvatar size={18} agency={membershipAgency} />
                      <Text size="sm" fw={500} flex={1} truncate>
                        {membershipAgency.name}
                      </Text>
                      {agency?.id === membershipAgency.id && <Icon name="check" />}
                    </Group>
                  </Menu.Item>
                ))}
            </>
          )}
          {agencyPolicies['createClient'] && (
            <Menu.Item onClick={toggleAddClient}>
              <Group gap="3xs">
                <Icon name="plus" />
                <Text size="sm" fw={500}>
                  {t('navbar.new_client')}
                </Text>
              </Group>
            </Menu.Item>
          )}
          <Menu.Label>{t('navbar.user')}</Menu.Label>
          <Menu.Item component={Link} to="/settings/user">
            <Group gap="3xs">
              <Icon name="userCircle" />
              <Text size="sm" fw={500}>
                {t('navbar.profile')}
              </Text>
            </Group>
          </Menu.Item>
          {me?.isImpersonating && (
            <Menu.Item
              onClick={async () => {
                await stopImpersonating();
                window.location.href = '/';
              }}
            >
              <Group gap="3xs">
                <Icon name="userOff" />
                <Text size="sm" fw={500}>
                  {t('navbar.stop-impersonating')}
                </Text>
              </Group>
            </Menu.Item>
          )}
          <Menu.Item component={Link} to="/auth/logout">
            <Group gap="3xs">
              <Icon name="logout" />
              <Text size="sm" fw={500}>
                {t('navbar.logout')}
              </Text>
            </Group>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      {addClientOpened && <ClientModal onClose={closeAddClient} />}
    </>
  );
};
